import useLocalization from "@/hooks/useLocalization";
import { Button, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { format, startOfMonth } from "date-fns";
import { useMemo, useRef, useState } from "react";

type YearMonthButtonProps = {
  displayedMonth: Date;
  isMobile: boolean;
  minMonthStart: Date | null;
  maxMonthStart: Date | null;
  onMonthChange: (month: Date) => void;
};

export default function YearMonthButton({
  displayedMonth,
  isMobile,
  minMonthStart,
  maxMonthStart,
  onMonthChange,
}: YearMonthButtonProps) {
  const { adapterLocale } = useLocalization();
  const [isMonthPickerOpen, setMonthPickerOpen] = useState(false);
  const [pendingMonth, setPendingMonth] = useState<Date | null>(null);
  const pendingMonthRef = useRef<Date | null>(null);
  const monthButtonRef = useRef<HTMLButtonElement | null>(null);
  const monthStart = useMemo(
    () => startOfMonth(displayedMonth),
    [displayedMonth],
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ flexWrap: "nowrap", minWidth: 0 }}
    >
      <Button
        color="inherit"
        variant="text"
        onClick={() => {
          setPendingMonth(displayedMonth);
          pendingMonthRef.current = displayedMonth;
          setMonthPickerOpen(true);
        }}
        sx={{
          textTransform: "none",
          paddingX: 0.5,
          flexShrink: 0,
          width: isMobile ? "120px" : "196px",
          justifyContent: "flex-start",
        }}
        ref={monthButtonRef}
      >
        <Typography
          variant="h5"
          noWrap
          sx={{ fontWeight: 700, minWidth: 0, textAlign: "center" }}
        >
          {format(monthStart, isMobile ? "MMM yyyy" : "MMMM yyyy", {
            locale: adapterLocale,
          })}
        </Typography>
      </Button>
      <DatePicker
        open={isMonthPickerOpen}
        onOpen={() => {
          setPendingMonth(displayedMonth);
          pendingMonthRef.current = displayedMonth;
          setMonthPickerOpen(true);
        }}
        onClose={() => {
          setMonthPickerOpen(false);
          if (pendingMonthRef.current) {
            onMonthChange(startOfMonth(pendingMonthRef.current));
          }
          setPendingMonth(null);
          pendingMonthRef.current = null;
        }}
        openTo="month"
        views={["year", "month"]}
        value={pendingMonth ?? displayedMonth}
        onChange={(value) => {
          if (!value) return;
          const nextValue = startOfMonth(value);
          if (minMonthStart && nextValue.getTime() < minMonthStart.getTime()) {
            setPendingMonth(minMonthStart);
            pendingMonthRef.current = minMonthStart;
            return;
          }
          if (maxMonthStart && nextValue.getTime() > maxMonthStart.getTime()) {
            setPendingMonth(maxMonthStart);
            pendingMonthRef.current = maxMonthStart;
            return;
          }
          setPendingMonth(nextValue);
          pendingMonthRef.current = nextValue;
        }}
        onAccept={(value) => {
          if (!value) return;
          const nextValue = startOfMonth(value);
          onMonthChange(nextValue);
          setPendingMonth(null);
          pendingMonthRef.current = null;
        }}
        minDate={minMonthStart ?? undefined}
        maxDate={maxMonthStart ?? undefined}
        slotProps={{
          textField: {
            sx: {
              width: 0,
              height: 0,
              padding: 0,
              margin: 0,
              opacity: 0,
            },
          },
          popper: {
            placement: "bottom-start",
            anchorEl: monthButtonRef.current,
          },
        }}
      />
    </Stack>
  );
}
